import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { playChime } from "../lib/chime";
import { FlowerOfLife } from "../components/SacredGeometry";
import { Check, Sparkles } from "lucide-react";

const PLANS = [
  {
    id: "monthly",
    name: "The Seeker's Path",
    price: "$33",
    cadence: "per month",
    jp: "月",
    tagline: "Walk gently, month by month",
    perks: ["Full access to all four degrees", "Every video transmission & study scroll", "Attunement ceremonies & certificates", "Cancel anytime with grace"],
  },
  {
    id: "annual",
    name: "The Devotee's Journey",
    price: "$288",
    cadence: "per year",
    jp: "年",
    featured: true,
    tagline: "Commit to the full path & save",
    perks: ["Everything in the Seeker's Path", "Two months offered freely", "Priority to new teachings", "A printed Master lineage scroll"],
  },
];

export default function Membership() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const active = user && user.subscription?.status === "active";

  const subscribe = async (plan) => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoading(plan);
    try {
      const { data } = await api.post("/subscription/activate", { plan });
      setUser(data);
      playChime();
      toast.success("The sanctuary opens fully to you. Blessings on your path.");
      navigate("/courses");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(null);
    }
  };

  const cancel = async () => {
    setLoading("cancel");
    try {
      const { data } = await api.post("/subscription/cancel");
      setUser(data);
      toast("Your membership has been released. You are always welcome to return.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 relative bg-sanctuary-deep">
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] aura breathe pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <FlowerOfLife className="w-16 mx-auto text-gold/40 mb-6" />
          <div className="eyebrow mb-4">Sacred Membership</div>
          <h1 className="font-display text-5xl text-parchment font-light">Choose your commitment</h1>
          <p className="mt-5 text-text-muted leading-relaxed">One membership unlocks the entire path — Shoden, Okuden, Shinpiden and the Master degree. No hidden gates. Only your own pace.</p>
        </div>

        {active && (
          <div className="max-w-xl mx-auto mb-12 rounded-2xl bg-sage/10 border border-sage/30 p-6 text-center" data-testid="membership-active-banner">
            <p className="text-sage-light">You are a cherished member on the <span className="capitalize font-medium">{user.subscription.plan}</span> path.</p>
            <button onClick={cancel} disabled={loading === "cancel"} className="mt-3 text-sm text-text-muted hover:text-parchment underline transition-colors" data-testid="cancel-subscription-button">
              {loading === "cancel" ? "Releasing…" : "Release my membership"}
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {PLANS.map((p) => (
            <div key={p.id} className={`relative rounded-3xl p-9 transition-transform duration-500 hover:-translate-y-1 ${p.featured ? "bg-sanctuary-card border-2 border-gold/40 gold-glow" : "bg-sanctuary-card gold-border"}`} data-testid={`plan-${p.id}`}>
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gold text-sanctuary-deep text-xs font-medium tracking-wide flex items-center gap-1">
                  <Sparkles size={12} /> Most devoted
                </div>
              )}
              <div className="font-jp text-4xl text-gold/30 absolute top-7 right-8">{p.jp}</div>
              <div className="eyebrow mb-3">{p.name}</div>
              <div className="flex items-end gap-2 mb-1">
                <span className="font-display text-5xl text-parchment">{p.price}</span>
                <span className="text-text-muted text-sm mb-2">{p.cadence}</span>
              </div>
              <p className="text-text-muted text-sm italic mb-7">{p.tagline}</p>
              <div className="flex flex-col gap-3 mb-8">
                {p.perks.map((perk) => (
                  <div key={perk} className="flex items-start gap-3 text-sm text-parchment/90">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    {perk}
                  </div>
                ))}
              </div>
              <button
                onClick={() => subscribe(p.id)}
                disabled={loading === p.id || (active && user.subscription.plan === p.id)}
                className={`w-full py-3.5 rounded-full font-medium transition-colors disabled:opacity-60 ${p.featured ? "bg-gold text-sanctuary-deep hover:bg-gold-hover" : "gold-border text-parchment hover:bg-gold/10"}`}
                data-testid={`subscribe-${p.id}-button`}
              >
                {active && user.subscription.plan === p.id ? "Your current path" : loading === p.id ? "Opening…" : user ? "Choose this path" : "Begin — free to join"}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-text-dim text-xs mt-10">Payments are simulated for now — activation is instant so you may explore every teaching.</p>
      </div>
    </div>
  );
}
